import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, useNavigate, useLocation } from "react-router-dom";
import routes from "./routes";
import { AUTH0_CLIENT_ID, AUTH0_DOMAIN } from "./config";
import { UserProvider } from "@unifiedpatents/sso";
import { ConfigProvider } from "antd";
import enUS from "antd/es/locale/en_US";
import { HelmetProvider } from "react-helmet-async";
import { SWRConfig } from "swr";
import "./app.css";

const App = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ToSHtmlLink = "<a href='//www.unifiedpatents.com/term-of-services' target='_blank' rel='noopener noreferrer'>Terms of Use</a>";
  const PPHtmlLink = "<a href='//www.unifiedpatents.com/privacy-policy' target='_blank' rel='noopener noreferrer'>Privacy Policy</a>";

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const userProviderProps = {
    clientId: AUTH0_CLIENT_ID,
    domain: AUTH0_DOMAIN,
    allowSignUp: true,
    languageDictionary: {
      title: "Portal - Unified Patents",
      signUpTerms: `I agree to the ${ToSHtmlLink} and ${PPHtmlLink}.`,
      socialLoginInstructions: `<small>By signing in via Google and LinkedIn, you agree to the ${ToSHtmlLink} and ${PPHtmlLink}.</small>`
    },
    provideUserStatus: metadata => {
      return {
        isAdmin: metadata.authorization.roles.indexOf("admin") >= 0,
        canAccessData: metadata.authorization.roles.indexOf("member-data") >= 0
      };
    },
    onAuthenticated: auth => {
      navigate(auth.state, { replace: true });
    }
  };

  const theme = {
    components: {
      Menu: {
        itemBg: "transparent",
        lineWidth: 0,
        itemHeight: 36,
        itemPaddingInline: 12,
        itemMarginBlock: 2,
        itemMarginInline: 2,
        horizontalLineHeight: "40px",
        iconMarginInlineEnd: 8
      }
    }
  }

  return (
    <ConfigProvider
      componentSize="small"
      locale={enUS}
      theme={theme}
      card={{
        styles: {
          root: {
            borderRadius: 10,
            borderColor: "rgba(15, 23, 42, 0.08)",
            boxShadow: "0 2px 4px rgba(15, 23, 42, 0.04), 0 12px 20px -16px rgba(15, 23, 42, 0.18)"
          }
        }
      }}
      button={{
        styles: {
          root: {
            borderColor: "rgba(15, 23, 42, 0.10)",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05), 0 3px 6px -4px rgba(15, 23, 42, 0.12)"
          }
        }
      }}
      tag={{
        styles: {
          root: {
            marginInlineEnd: 8,
            borderRadius: 16,
            borderColor: "rgba(15, 23, 42, 0.08)",
            boxShadow: "0 1px 1px rgba(15, 23, 42, 0.04)"
          }
        }
      }}
    >
      <HelmetProvider>
        <SWRConfig value={{ revalidateOnFocus: false, dedupingInterval: 60_000 }}>
          <UserProvider {...userProviderProps}>
            <Routes>{routes}</Routes>
          </UserProvider>
        </SWRConfig>
      </HelmetProvider>
    </ConfigProvider>
  );
};

const Main = () => (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

const rootElement = document.getElementById("root");

if (rootElement) {
  const root = createRoot(rootElement);
  root.render(<Main />);
}
